<template src="./PersonalDetail.template.html"></template>

<script setup>
import { onMounted, onUnmounted, watch, ref, computed } from 'vue'
import { useRoute, useRouter, RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { usePersonalAd, useMyPersonals } from '@/composables/usePersonals'
import { ArrowLeft, MessageCircle, Clock, Send, AlertTriangle, User, Eye } from 'lucide-vue-next'

// Components
import PageHeader from '../components/ui/PageHeader.vue'
import LayoutMain from '../components/ui/LayoutMain.vue'
import Button from '../components/ui/Button.vue'
import Card from '../components/ui/Card.vue'
import Text from '../components/ui/Text.vue'
import Heading from '../components/ui/Heading.vue'
import Textarea from '../components/ui/Textarea.vue'
import LoadingSpinner from '../components/ui/LoadingSpinner.vue'
import ErrorDisplay from '../components/ui/ErrorDisplay.vue'
import PersonalsTopBar from '../components/personals/PersonalsTopBar.vue'

// Composables
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const adId = route.params.id

const {
  replyForm,
  replyErrors,
  ad,
  replies,
  isLoading,
  error,
  canReply,
  loadAd,
  loadReplies,
  validateReply,
  submitReply,
  goBack,
  cleanup,
  clearError
} = usePersonalAd(adId)
const { loadMyReplies, myReplies } = useMyPersonals()

// Local state
const showReplyForm = ref(false)
const replySuccess = ref('')
const replyError = ref('')
const characterCount = ref(0)
// Track latest reply ID to enable highlighting on MyReplies page
const lastReplyId = ref(null)

// Convenience helper to render reply count via i18n without ICU plural syntax
function renderRepliesCount(count) {
  return t('personals.repliesCount', { count })
}

// Methods
function handleShowReplyForm() {
  if (!canReply.value) {
    replyError.value = t('personals.replyLimitReached')
    return
  }
  showReplyForm.value = true
}

function handleCancelReply() {
  showReplyForm.value = false
  replyForm.value.message = ''
  replyErrors.value = {}
  replyError.value = ''
}

async function handleSubmitReply() {
  replyError.value = ''
  replySuccess.value = ''

  const result = await submitReply()

  if (result.success) {
    // Store reply ID for highlighting
    lastReplyId.value = result.reply?.id ?? null
    replySuccess.value = result.message
    showReplyForm.value = false
    setTimeout(() => {
      replySuccess.value = ''
    }, 3000)
  } else {
    replyError.value = result.message
  }
}

function updateCharacterCount() {
  characterCount.value = replyForm.value.message.length
}

function formatTimeAgo(dateString) {
  const date = new Date(dateString)
  const now = new Date()
  const diffInMinutes = Math.floor((now - date) / (1000 * 60))

  // Debug: validate i18n keys presence during dev to diagnose missing 'time.*'
  if (import.meta.env.DEV) {
    try {
      console.debug('[PersonalDetail/formatTimeAgo] Checking i18n time keys', {
        hasJustNow: !!t('common.time.justNow'),
        hasMinutesAgo: !!t('common.time.minutesAgo', { count: 1 }),
        hasHoursAgo: !!t('common.time.hoursAgo', { count: 1 }),
        hasDaysAgo: !!t('common.time.daysAgo', { count: 1 })
      })
    } catch (e) {
      console.debug('[PersonalDetail/formatTimeAgo] i18n check failed', e)
    }
  }

  // Keys live under the "common" namespace in en/index.js, so use "common.time.*"
  if (diffInMinutes < 1) return t('common.time.justNow')
  if (diffInMinutes < 60) return t('common.time.minutesAgo', { count: diffInMinutes })

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) return t('common.time.hoursAgo', { count: diffInHours })

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) return t('common.time.daysAgo', { count: diffInDays })

  return date.toLocaleDateString()
}

function formatPostDate(dateString) {
  const date = new Date(dateString)
  const now = new Date()
  const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))

  if (diffInDays === 0) return t('common.time.today')
  if (diffInDays === 1) return t('common.time.yesterday')
  if (diffInDays < 7) return t('common.time.daysAgo', { count: diffInDays })

  return date.toLocaleDateString()
}

function viewProfile(userId) {
  // Navigate to user profile page
  router.push({ name: 'user-profile', params: { id: userId } })
}

function getAvatarImage(thumbnailUrl) {
  // Handle avatar image URL - this might need adjustment based on your image handling
  return thumbnailUrl
}

// Lifecycle
const userHasReplied = computed(() => {
  const reply = myReplies.value.find(r => r.personal_ads.id === ad.value?.id)
  if (reply) {
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    lastReplyId.value = reply.id
  }
  return !!reply
})

onMounted(async () => {
  await loadAd()
  if (ad.value && ad.value.is_mine) {
    await loadReplies()
  } else {
    await loadMyReplies()
  }
})

onUnmounted(() => {
  cleanup()
})

// Watch for character count updates
watch(() => replyForm.value.message, updateCharacterCount)

// Watch for errors and auto-clear them
watch(error, newError => {
  if (newError) {
    setTimeout(() => {
      clearError()
    }, 5000)
  }
})
</script>

<style scoped>
.personal-detail {
  min-height: 100vh;
}

.ad-content {
  line-height: 1.7;
  white-space: pre-wrap;
}

.reply-form {
  position: sticky;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-top: 1px solid var(--neutral-200);
  padding: 1rem;
  margin: 0 -1rem -1rem -1rem;
}

.character-counter {
  font-size: 0.75rem;
  color: var(--neutral-500);
}

.character-counter.warning {
  color: var(--accent-coral);
}

.character-counter.danger {
  color: var(--red-500);
}

.replies-section {
  border-top: 1px solid var(--neutral-200);
  padding-top: 1.5rem;
  margin-top: 1.5rem;
}

.reply-item {
  border-left: 3px solid var(--accent-coral);
  padding-left: 1rem;
  margin-bottom: 1rem;
}

.success-message {
  background: var(--emerald-50);
  color: var(--emerald-700);
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--emerald-200);
  margin-bottom: 1rem;
}

.error-message {
  background: var(--red-50);
  color: var(--red-700);
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--red-200);
  margin-bottom: 1rem;
}

/* Styles for the new replies section */
.user-avatar-container {
  flex-shrink: 0;
}

.user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--gradient-linen);
}

.user-avatar-placeholder {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--rogue-vibrant-green), var(--rogue-rich-coral));
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-name-link {
  font-weight: 600;
  color: var(--rogue-deep-forest);
  text-decoration: none;
  transition: color 0.2s ease;
}

.user-name-link:hover {
  color: var(--rogue-vibrant-green);
  text-decoration: underline;
}

.view-profile-btn {
  color: var(--rogue-vibrant-green);
  transition: all 0.2s ease;
}

.view-profile-btn:hover {
  background: var(--overlay-green-light);
  transform: translateY(-1px);
}
</style>
